import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { postsAPI, categoriasAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const FormularioPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categorias, setCategorias] = useState([]);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    contenido: '',
    imagen: '',
    palabrasClave: '',
    publicado: false,
    categoriasSeleccionadas: []
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!hasRole('administrador')) {
      navigate('/estudios');
      return;
    }
    cargarCategorias();
    if (id) {
      cargarPost();
    }
  }, [id]);

  const cargarCategorias = async () => {
    try {
      const response = await categoriasAPI.listar();
      setCategorias(response.data.data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const cargarPost = async () => {
    try {
      setLoading(true);
      const response = await postsAPI.getPorId(id); // Cargar por ID para edición
      const post = response.data.data;
      
      setFormData({
        titulo: post.titulo || '',
        descripcion: post.descripcion || '',
        contenido: post.contenido || '',
        imagen: post.imagen || '',
        palabrasClave: post.palabrasClave || '',
        publicado: post.publicado || false,
        categoriasSeleccionadas: post.categorias?.map(c => c.id) || []
      });
    } catch (error) {
      console.error('Error al cargar post:', error);
      navigate('/admin/posts');
    } finally {
      setLoading(false);
    }
  };

  const validarFormulario = () => {
    const newErrors = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'El título es requerido';
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es requerida';
    }

    if (!formData.contenido.trim() || formData.contenido === '<p><br></p>') {
      newErrors.contenido = 'El contenido es requerido';
    }

    if (formData.categoriasSeleccionadas.length === 0) {
      newErrors.categorias = 'Selecciona al menos una categoría';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        categorias: formData.categoriasSeleccionadas
      };

      if (id) {
        await postsAPI.actualizar(id, dataToSend);
      } else {
        await postsAPI.crear(dataToSend);
      }

      navigate('/admin/posts');
    } catch (error) {
      console.error('Error al guardar post:', error);
      // Mostrar errores de validación en el formulario si existen
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategoriaToggle = (categoriaId) => {
    setFormData(prev => ({
      ...prev,
      categoriasSeleccionadas: prev.categoriasSeleccionadas.includes(categoriaId)
        ? prev.categoriasSeleccionadas.filter(id => id !== categoriaId)
        : [...prev.categoriasSeleccionadas, categoriaId]
    }));
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  if (loading && id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/admin/posts"
            className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
          >
            <FiArrowLeft className="mr-2" />
            Volver al panel
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {id ? 'Editar Post' : 'Nuevo Post'}
          </h1>
        </div>
      </div>

      {/* Formulario */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            {/* Título */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  errors.titulo ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Título del estudio bíblico"
              />
              {errors.titulo && <p className="mt-1 text-sm text-red-600">{errors.titulo}</p>}
            </div>

            {/* Descripción */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción breve *
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                rows="3"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 ${
                  errors.descripcion ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Descripción que aparecerá en el listado"
              />
              {errors.descripcion && <p className="mt-1 text-sm text-red-600">{errors.descripcion}</p>}
            </div>

            {/* Imagen */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de imagen destacada
              </label>
              <input
                type="url"
                value={formData.imagen}
                onChange={(e) => setFormData({ ...formData, imagen: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              {formData.imagen && (
                <img
                  src={formData.imagen}
                  alt="Preview"
                  className="mt-2 h-32 object-cover rounded"
                />
              )}
            </div>

            {/* Palabras clave */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Palabras clave
              </label>
              <input
                type="text"
                value={formData.palabrasClave}
                onChange={(e) => setFormData({ ...formData, palabrasClave: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Separadas por comas: teología, profecía, nuevo testamento"
              />
            </div>

            {/* Categorías */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categorías * (selecciona una o más)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {categorias.map((categoria) => (
                  <label
                    key={categoria.id}
                    className={`flex items-center p-2 border rounded cursor-pointer transition ${
                      formData.categoriasSeleccionadas.includes(categoria.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.categoriasSeleccionadas.includes(categoria.id)}
                      onChange={() => handleCategoriaToggle(categoria.id)}
                      className="mr-2"
                    />
                    <span className="text-sm">{categoria.nombre}</span>
                  </label>
                ))}
              </div>
              {errors.categorias && <p className="mt-1 text-sm text-red-600">{errors.categorias}</p>}
            </div>

            {/* Editor de contenido */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenido *
              </label>
              <div className={`border rounded-lg ${errors.contenido ? 'border-red-500' : 'border-gray-300'}`}>
                <ReactQuill
                  theme="snow"
                  value={formData.contenido}
                  onChange={(value) => setFormData({ ...formData, contenido: value })}
                  modules={modules}
                  className="bg-white"
                  style={{ minHeight: '400px' }}
                />
              </div>
              {errors.contenido && <p className="mt-1 text-sm text-red-600">{errors.contenido}</p>}
            </div>

            {/* Publicado */}
            <div className="mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.publicado}
                  onChange={(e) => setFormData({ ...formData, publicado: e.target.checked })}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Publicar inmediatamente
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Si no está marcado, se guardará como borrador
              </p>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4">
            <Link
              to="/admin/posts"
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FiSave className="mr-2" />
              {loading ? 'Guardando...' : 'Guardar Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioPost;

